import React, { useEffect, useRef, useState } from 'react';
import {
  DollarSign, MoreVertical, Trash2, RefreshCw, Check,
  Calendar, ChevronLeft, ChevronRight, Heart, Clock,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

import DarkTooltip from '../ui/DarkTooltip';
import FlagIcon from '../voice/FlagIcon';
import {
  calculateUploadsPerMonth,
  daysSince,
  formatFreshness,
  formatK,
  formatPostingTimeShort,
  formatRelativeTime,
  getAvatarColor,
  getChannelClassification,
  getClassificationLabelKey,
  getCountryCode,
  getInitials,
  getLanguageName,
  getTierColor,
  getVideoThumbnail,
  TIER_TOOLTIPS,
} from './utils';

// Faceless % helpers — sintese visual da metrica:
//   >= 70  -> verde   (forte indicio de faceless)
//   40-69  -> amarelo (zona ambigua)
//   < 40   -> neutro  (provavelmente nao-faceless)
//   null   -> '—'     (sem dado)
function renderFacelessValue(score, isPrivate) {
  if (isPrivate) return '•••';
  if (score === null || score === undefined) return '—';
  return `${Math.round(score)}%`;
}
function getFacelessColor(score, isPrivate) {
  if (isPrivate || score === null || score === undefined) return null;
  if (score >= 70) return 'var(--dp-green-500)';
  if (score >= 40) return 'var(--dp-yellow-500)';
  return null;
}

/**
 * ChannelCard — card individual do Niche Finder.
 *
 * Layout (mockup):
 *   [Avatar] Nome  [$]  [🇧🇷]                   [↗]
 *           Categoria · Subcategoria
 *
 *   [5 thumbs 16:9 com views overlay]
 *
 *   ┌ SUBS | VIEWS | VÍDEOS | MULTI ┐
 *
 *   📅 142 dias postando   3.2 vídeos/mês
 *
 * Comportamentos:
 *   - Click neutro -> marca visitado + abre channel.url
 *   - Click numa thumb -> abre o video
 *   - Privacy mode -> mascara nome/numeros
 *   - Visited -> opacity reduzido + badge
 *   - Super admin -> menu de 3 pontos com Excluir / Re-processar
 */
const ChannelCard = ({
  channel,
  isVisited = false,
  isPrivacyMode = false,
  isSuperAdmin = false,
  isFavorited = false,
  onVisit,
  onDelete,
  onReenrich,
  onToggleFavorite,
}) => {
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  const {
    channel_id,
    title,
    channel_url,
    thumbnail,
    subscribers,
    total_views,
    num_uploads,
    is_monetized,
    language_detected,
    primary_category,
    categories,
    subcategory,
    top_videos,
    posting_age_days,
    first_video_at,
    faceless_score,
    imported_at,
    last_enriched_at,
    avg_views_last_30,
  } = channel || {};

  const ytUrl = channel_url || (channel_id ? `https://www.youtube.com/channel/${channel_id}` : '#');

  // Privacy mask helpers
  const maskedTitle = isPrivacyMode ? '••••••••••' : (title || t('viralNiches.channel_no_name'));
  const formatNum = (n) => (isPrivacyMode ? '•••' : formatK(n));

  // Carrossel de top_videos: 3 visiveis por vez + setinhas pra navegar.
  // Reserva sempre o mesmo espaco (altura fixa) — slots vazios sao sutis.
  const allVideos = Array.isArray(top_videos) ? top_videos : [];
  const VISIBLE_VIDEOS = 3;
  const [carouselStart, setCarouselStart] = useState(0);
  // Reseta carrossel quando o canal muda (ex: filtro recarrega lista)
  useEffect(() => { setCarouselStart(0); }, [channel_id]);
  const carouselMaxStart = Math.max(0, allVideos.length - VISIBLE_VIDEOS);
  const carouselClamped = Math.min(carouselStart, carouselMaxStart);
  const visibleVideos = allVideos.slice(carouselClamped, carouselClamped + VISIBLE_VIDEOS);
  const hasMoreVideos = carouselClamped + VISIBLE_VIDEOS < allVideos.length;
  const hasPrevVideos = carouselClamped > 0;

  // Categoria principal — preferencia: categories[0] -> primary_category
  const categoryLabel = (Array.isArray(categories) && categories.length > 0)
    ? categories[0]
    : (primary_category || null);

  // Postagem age — usa posting_age_days do backend OU calcula via first_video_at
  const postingDays = posting_age_days ?? daysSince(first_video_at);
  const uploadsPerMonth = calculateUploadsPerMonth(num_uploads, first_video_at);

  // Pacote 7.2: as cores funcionais antigas (postingDaysColor/uploadsColor por
  // faixa) sairam. Pacote 7.3 (papagaio fix): metricas e footer voltaram pro
  // neutro; a cor do tier fica so no top-bar (gradient), tag e hover da borda.

  // hasFooter controla DOIS aspectos da altura do card:
  //   1. alignSelf no grid: stretch se TEM footer (precisa ancorar
  //      footer no fundo via spacer), flex-start se NAO TEM (deixa o
  //      card encurtar pra altura natural — sem espaco vazio embaixo)
  //   2. Renderizacao do spacer flex:1 — so faz sentido empurrar o
  //      footer pro fundo se ele existe
  const hasAvgViews = avg_views_last_30 !== null && avg_views_last_30 !== undefined && avg_views_last_30 > 0;
  const hasFooter = (postingDays !== null && postingDays !== undefined)
                  || (uploadsPerMonth !== null && uploadsPerMonth !== undefined && uploadsPerMonth !== '')
                  || hasAvgViews
                  || !!imported_at;

  // Pacote 8C — freshness inline com "Adicionado ha X". Defensivo:
  //  - sem last_enriched_at -> null (canal nunca enriquecido)
  //  - last_enriched_at < imported_at -> null (dado JSON antigo, ruim)
  //  - diff < 1h -> null (dado fresco, redundante)
  // formatFreshness aplica os thresholds restantes e devolve {label, isStale}.
  const freshness = (() => {
    if (!last_enriched_at || !imported_at) return null;
    const enrichedMs = new Date(last_enriched_at).getTime();
    const importedMs = new Date(imported_at).getTime();
    if (!Number.isFinite(enrichedMs) || !Number.isFinite(importedMs)) return null;
    if (enrichedMs < importedMs) return null;
    return formatFreshness(last_enriched_at, t);
  })();

  // Classificacao funcional do canal (Explodindo/EmAlta/Promissor/Novo/Outlier).
  // Define a cor da top bar do card + a tag grande acima do footer + a cor de
  // hover da borda. null se canal nao se encaixa em nenhuma regra.
  const classification = getChannelClassification(channel);
  const classificationLabel = classification
    ? t(getClassificationLabelKey(classification.key))
    : null;
  // Pacote 7.2: cor UNICA do tier (TIER_COLORS). null em canal sem tier.
  const tierColor = getTierColor(classification?.key);

  const handleCardClick = (e) => {
    // Ignora clicks em areas com handler proprio (botoes, links, thumbs)
    if (e.defaultPrevented) return;
    if (e.target.closest('[data-no-card-click]')) return;
    if (isPrivacyMode) return;
    if (typeof onVisit === 'function') onVisit(channel_id);
    if (ytUrl && ytUrl !== '#') window.open(ytUrl, '_blank', 'noopener,noreferrer');
  };

  const handleThumbClick = (e, video) => {
    e.preventDefault();
    e.stopPropagation();
    if (isPrivacyMode) return;
    if (typeof onVisit === 'function') onVisit(channel_id);
    if (video?.videoId) {
      window.open(
        `https://www.youtube.com/watch?v=${video.videoId}`,
        '_blank', 'noopener,noreferrer',
      );
    }
  };

  return (
    <div
      onClick={handleCardClick}
      onMouseEnter={(e) => {
        // Hover: borda ganha a COR DO TIER (Pacote 7.2) ou --dp-border-medium.
        e.currentTarget.style.borderColor = tierColor || 'var(--dp-border-medium)';
        e.currentTarget.style.transform = 'translateY(-1px)';
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--dp-border-subtle)';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
      style={{
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        // Sempre estica ate a altura do tallest da row (consistencia
        // de layout > compactness). Quando o footer real nao renderiza,
        // o placeholder invisivel abaixo mantem a altura visual.
        alignSelf: 'stretch',
        height: '100%',
        boxSizing: 'border-box',
        background: 'var(--dp-bg-card)',
        border: '1px solid var(--dp-border-subtle)',
        borderRadius: 12,
        cursor: isPrivacyMode ? 'default' : 'pointer',
        position: 'relative',
        overflow: 'hidden',  // clipa a top bar de classificacao
        transition: 'border-color 0.2s ease, transform 0.2s ease',
      }}
    >
      {/* Top bar 2px com gradient da classificacao (padrao CanalManagerNew) */}
      {classification && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: classification.gradient,
            pointerEvents: 'none',
          }}
        />
      )}
      {/* ===== Header: avatar + coluna (nome+acoes / categoria+tag) — Pacote 5.1 ===== */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        {/* PARTE 5.1 — avatar com bandeira do idioma como badge sobreposto
            (canto superior direito); tooltip mostra o nome do idioma. */}
        <div style={{ position: 'relative', flexShrink: 0 }}>
          <Avatar
            channelId={channel_id}
            title={title}
            thumbnail={thumbnail}
            isPrivate={isPrivacyMode}
          />
          {language_detected && (
            // Pacote 7.4: wrapper ABSOLUTO posiciona o badge; o DarkTooltip
            // envolve um span EM FLUXO (com tamanho real). Antes o badge era
            // position:absolute direto dentro do DarkTooltip -> anchor 0x0 ->
            // o tooltip nunca aparecia.
            <div
              data-no-card-click
              style={{ position: 'absolute', top: -3, right: -3 }}
            >
              <DarkTooltip content={getLanguageName(language_detected, t)}>
                <span
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: '50%',
                    background: 'var(--dp-bg-card)',
                    border: '2px solid var(--dp-bg-card)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 0 0.5px var(--dp-border-medium)',
                    cursor: 'default',
                  }}
                >
                  <FlagIcon code={getCountryCode(language_detected)} size="13px" rounded />
                </span>
              </DarkTooltip>
            </div>
          )}
        </div>

        {/* Coluna de conteudo: 2 linhas — (nome + acoes) / (categoria + tag).
            Pacote 5.1: nome justificado no topo do avatar; categoria embaixo;
            tag de classificacao inline a direita da categoria (NUNCA no footer). */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>

          {/* Linha 1: nome + $ inline (esquerda) + acoes: favoritar, menu (direita).
              Pacote 8B: $ movido pra inline com o nome (mesma hierarquia visual do
              CategoryGroupedViewCompact) — antes ficava entre Heart e menu admin,
              longe do nome. */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0, flex: 1 }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'var(--dp-text-primary)',
                  fontFamily: 'var(--dp-font)',
                  lineHeight: 1.3,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                }}
                title={maskedTitle}
              >
                {maskedTitle}
              </h3>
              {/* Pacote 8B/8E — $ inline com o nome.
                  - true  -> verde   ('Monetizado')
                  - false -> vermelho ('Nao monetizado' — verificado pela ext)
                  - null/undefined -> nada (status desconhecido, ext nunca visitou)
                  Comparacao com === true/false (nao && / !) pra distinguir
                  false de null — senao $ vermelho apareceria pra desconhecido. */}
              {is_monetized === true && (
                <DarkTooltip content={t('viralNiches.monetized_tooltip')}>
                  <DollarSign
                    size={14}
                    style={{
                      color: 'var(--dp-green-500)',
                      flexShrink: 0,
                      cursor: 'help',
                    }}
                  />
                </DarkTooltip>
              )}
              {is_monetized === false && (
                <DarkTooltip content={t('viralNiches.not_monetized_tooltip')}>
                  <DollarSign
                    size={14}
                    style={{
                      color: 'var(--dp-red-500)',
                      flexShrink: 0,
                      cursor: 'help',
                    }}
                  />
                </DarkTooltip>
              )}
            </div>
            <div
              data-no-card-click
              style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}
            >
              {/* PARTE 5.2 — botao favoritar (coracao). */}
              {typeof onToggleFavorite === 'function' && (
                <DarkTooltip content={isFavorited ? t('viralNiches.favorite_remove') : t('viralNiches.favorite_add')}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onToggleFavorite(channel_id);
                    }}
                    aria-label={isFavorited ? t('viralNiches.favorite_remove') : t('viralNiches.favorite_add')}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      background: 'transparent',
                      border: 'none',
                      padding: 2,
                      cursor: 'pointer',
                      color: isFavorited ? 'var(--dp-red-500)' : 'var(--dp-text-tertiary)',
                      transition: 'color 0.15s, transform 0.15s',
                    }}
                  >
                    <Heart size={16} fill={isFavorited ? 'var(--dp-red-500)' : 'none'} />
                  </button>
                </DarkTooltip>
              )}
              {isSuperAdmin && (
                <div style={{ position: 'relative' }} ref={menuRef}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setMenuOpen((v) => !v);
                    }}
                    aria-label="menu"
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 6,
                      background: 'transparent',
                      border: 'none',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'var(--dp-text-secondary)',
                      cursor: 'pointer',
                    }}
                  >
                    <MoreVertical size={14} />
                  </button>
                  {menuOpen && (
                    <AdminMenu
                      onClose={() => setMenuOpen(false)}
                      onReenrich={() => {
                        setMenuOpen(false);
                        onReenrich && onReenrich(channel_id);
                      }}
                      onDelete={() => {
                        setMenuOpen(false);
                        onDelete && onDelete(channel_id);
                      }}
                    />
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Linha 2: categoria · subcategoria (esquerda) + tag de classificacao (direita). */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
            <div
              style={{
                fontSize: 11,
                color: 'var(--dp-text-tertiary)',
                fontFamily: 'var(--dp-font)',
                fontWeight: 500,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
              title={[categoryLabel, subcategory].filter(Boolean).join(' · ')}
            >
              {[categoryLabel, subcategory].filter(Boolean).join(' · ')}
            </div>
            {classification && (
              <DarkTooltip
                content={TIER_TOOLTIPS[classification.key] ? t(TIER_TOOLTIPS[classification.key]) : ''}
              >
                <span
                  style={{
                    flexShrink: 0,
                    fontSize: 9,
                    fontWeight: 600,
                    color: classification.color,
                    background: classification.bg,
                    border: `0.5px solid ${classification.border}`,
                    borderRadius: 6,
                    padding: '2px 8px',
                    whiteSpace: 'nowrap',
                    fontFamily: 'var(--dp-font)',
                  }}
                >
                  {classificationLabel}
                </span>
              </DarkTooltip>
            )}
          </div>
        </div>
      </div>

      {/* ===== Carrossel de 3 thumbnails — altura RESERVADA mesmo sem videos.
          Slots vazios (canal com 0-2 videos) ficam sutis pra manter layout uniforme.
          Setinhas aparecem quando ha mais videos do que cabem nas 3 visiveis. */}
      <div
        data-no-card-click
        style={{
          position: 'relative',
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 4,
        }}
      >
        {Array.from({ length: VISIBLE_VIDEOS }).map((_, i) => {
          const v = visibleVideos[i];
          if (!v) {
            // Slot vazio — placeholder sutil pra reservar espaco
            return (
              <div
                key={`empty-${i}`}
                style={{
                  aspectRatio: '16 / 9',
                  background: 'var(--dp-bg-card-hover)',
                  opacity: 0.2,
                  borderRadius: 6,
                }}
              />
            );
          }
          return (
            <ThumbCell
              key={v.videoId || `vid-${i}`}
              video={v}
              isPrivate={isPrivacyMode}
              onClick={(e) => handleThumbClick(e, v)}
            />
          );
        })}
        {hasPrevVideos && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setCarouselStart((s) => Math.max(0, s - 1));
            }}
            aria-label="previous videos"
            style={{
              position: 'absolute',
              left: -4,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.7)',
              border: 'none',
              color: '#fff',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
              padding: 0,
            }}
          >
            <ChevronLeft size={14} />
          </button>
        )}
        {hasMoreVideos && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setCarouselStart((s) => Math.min(carouselMaxStart, s + 1));
            }}
            aria-label="next videos"
            style={{
              position: 'absolute',
              right: -4,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 24,
              height: 24,
              borderRadius: '50%',
              background: 'rgba(0,0,0,0.7)',
              border: 'none',
              color: '#fff',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2,
              padding: 0,
            }}
          >
            <ChevronRight size={14} />
          </button>
        )}
      </div>

      {/* ===== Bloco de metricas: 4 colunas ===== */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(4, 1fr)',
          gap: 0,
          background: 'var(--dp-bg-secondary)',
          borderRadius: 10,
          border: '1px solid var(--dp-border-subtle)',
          padding: '6px 0',
        }}
      >
        {/* Pacote 7.3 (papagaio fix): numeros voltam pro branco neutro
            (MetricCell default = var(--dp-text-primary)); so o FACELESS
            mantem cor semantica (verde/amarelo via getFacelessColor). */}
        <MetricCell
          value={formatNum(subscribers)}
          label={t('viralNiches.card_subs_label')}
        />
        <MetricCell
          value={formatNum(total_views)}
          label={t('viralNiches.card_views_label')}
          withDivider
        />
        <MetricCell
          value={isPrivacyMode ? '•••' : (num_uploads ?? '—')}
          label={t('viralNiches.card_videos_label')}
          withDivider
        />
        <MetricCell
          value={renderFacelessValue(faceless_score, isPrivacyMode)}
          label={t('viralNiches.card_faceless_label')}
          withDivider
          highlightColor={getFacelessColor(faceless_score, isPrivacyMode)}
          tooltipTitle={t('viralNiches.faceless_tooltip_title')}
          tooltipDesc={t('viralNiches.faceless_tooltip_desc')}
        />
      </div>

      {/* Footer (Pacote 7.2) — empurrado pro fundo via marginTop:auto.
          L1 = dados compactos na COR DO TIER; L2 = "Adicionado ha X".
          A tag de classificacao fica no HEADER (linha 2), nunca aqui. */}
      {hasFooter ? (
        <div
          style={{
            marginTop: 'auto',
            paddingTop: 8,
            borderTop: '0.5px solid var(--dp-border-subtle)',
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
          }}
        >
          {/* L1: tudo numa linha; cor = tier (ou neutro em canal sem tier).
              nowrap + ellipsis: nunca quebra embaixo. Esconde campos NULL/0. */}
          <div
            style={{
              fontSize: 11,
              color: 'var(--dp-text-secondary)',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            <Calendar size={11} style={{ flexShrink: 0 }} />
            {postingDays !== null && postingDays !== undefined && (
              <span>{formatPostingTimeShort(postingDays)} postando</span>
            )}
            {uploadsPerMonth && (
              <>
                {postingDays !== null && postingDays !== undefined && (
                  <span style={{ opacity: 0.4 }}>·</span>
                )}
                <span>{uploadsPerMonth} vid/mês</span>
              </>
            )}
            {hasAvgViews && (
              <>
                {(postingDays != null || uploadsPerMonth) && (
                  <span style={{ opacity: 0.4 }}>·</span>
                )}
                <DarkTooltip content={t('viralNiches.card_avg_views_tooltip')}>
                  <span>
                    {isPrivacyMode ? '•••' : formatK(avg_views_last_30)} views/mês
                  </span>
                </DarkTooltip>
              </>
            )}
          </div>

          {/* L2: "Adicionado ha X" (relativo) + check de visitado. */}
          <div
            style={{
              fontSize: 10,
              color: 'var(--dp-text-tertiary)',
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
          >
            {imported_at && (
              <>
                <Clock size={10} style={{ flexShrink: 0 }} />
                <span>Adicionado {formatRelativeTime(imported_at, t)}</span>
                {/* Pacote 8C — "atualizado ha Xh/dias" inline. Cor laranja
                    sutil quando o dado passa de 7 dias (isStale), neutro
                    nos casos frescos. Renderiza so quando faz sentido
                    (formatFreshness retorna null pra <1h ou dado invalido). */}
                {freshness && (
                  <span
                    style={{
                      marginLeft: 6,
                      color: freshness.isStale
                        ? 'var(--dp-orange-500)'
                        : 'var(--dp-text-tertiary)',
                      opacity: freshness.isStale ? 0.85 : 1,
                    }}
                  >
                    · {freshness.label}
                  </span>
                )}
              </>
            )}
            {isVisited && (
              <DarkTooltip content={t('viralNiches.card_visited_badge')}>
                <span style={{ display: 'inline-flex', alignItems: 'center', color: 'var(--dp-blue-400, #3AA5FF)', opacity: 0.75, marginLeft: imported_at ? 4 : 0 }}>
                  <Check size={12} />
                </span>
              </DarkTooltip>
            )}
          </div>
        </div>
      ) : (
        <div aria-hidden="true" style={{ marginTop: 'auto', height: 32, visibility: 'hidden', pointerEvents: 'none' }} />
      )}
    </div>
  );
};

// ============================================================
// Sub-componentes locais
// ============================================================

// AvatarFallback (Pacote 7) — circulo colorido + iniciais (padrao YouTube/
// Material) pra quando o avatar e' null/invalido OU o <img> falha em carregar.
// Cor deterministica via getAvatarColor(channelId) (paleta da app, nunca cinza).
// A bandeira de idioma fica sobreposta no wrapper do card — continua aparecendo.
const AvatarFallback = ({ channelId, title, isPrivate, size = 44 }) => (
  <div
    style={{
      width: size, height: size, borderRadius: '50%',
      background: getAvatarColor(channelId),
      color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontWeight: 600, fontSize: 14, flexShrink: 0,
      fontFamily: 'var(--dp-font)',
    }}
  >
    {isPrivate ? '?' : getInitials(title)}
  </div>
);

const Avatar = ({ channelId, title, thumbnail, isPrivate }) => {
  const [imgError, setImgError] = useState(false);
  const showImage = !!thumbnail && !imgError && !isPrivate;
  if (!showImage) {
    return <AvatarFallback channelId={channelId} title={title} isPrivate={isPrivate} />;
  }
  return (
    <img
      src={thumbnail}
      alt={title || '?'}
      onError={() => setImgError(true)}
      style={{
        width: 44, height: 44, borderRadius: 22, objectFit: 'cover',
        flexShrink: 0,
      }}
    />
  );
};

const ThumbCell = ({ video, isPrivate, onClick }) => {
  const src = getVideoThumbnail(video?.videoId, video?.thumbnail);
  const [imgFailed, setImgFailed] = useState(false);
  const [inView, setInView] = useState(false);
  const containerRef = useRef(null);

  // Camada C do carregamento inteligente: IntersectionObserver dispara
  // o download da thumbnail SO quando o card entra no viewport (300px de
  // antecipacao). Em escala (600k canais) evita 1000+ requests
  // simultaneas no CDN do YouTube. loading="lazy" nativo ainda atua
  // como fallback caso o browser nao suporte IO.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    if (inView) return; // ja entrou no viewport, parar de observar
    if (typeof IntersectionObserver === 'undefined') {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        observer.disconnect();
      }
    }, { rootMargin: '500px' });
    observer.observe(node);
    return () => observer.disconnect();
  }, [inView]);

  const showImage = src && !isPrivate && !imgFailed && inView;
  // Cor deterministica baseada no videoId pra gradient de fallback
  const fallbackColor = getAvatarColor(video?.videoId || video?.title || '');
  const fallbackTitle = isPrivate ? '?' : (video?.title || '').slice(0, 40);
  return (
    <div
      ref={containerRef}
      onClick={onClick}
      style={{
        position: 'relative',
        aspectRatio: '16 / 9',
        background: showImage
          ? 'var(--dp-bg-input)'
          : `linear-gradient(135deg, ${fallbackColor}, var(--dp-bg-card))`,
        borderRadius: 6,
        overflow: 'hidden',
        cursor: isPrivate ? 'default' : 'pointer',
      }}
    >
      {showImage ? (
        <img
          src={src}
          alt={video?.title || ''}
          loading="lazy"
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={() => setImgFailed(true)}
        />
      ) : (
        // Fallback: gradient + titulo do video centralizado
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 6,
            textAlign: 'center',
            fontSize: 9,
            fontWeight: 600,
            color: '#fff',
            textShadow: '0 1px 2px rgba(0,0,0,0.5)',
            lineHeight: 1.2,
            overflow: 'hidden',
            fontFamily: 'var(--dp-font)',
          }}
        >
          {fallbackTitle}
        </div>
      )}
      {!isPrivate && typeof video?.views === 'number' && video.views > 0 && (
        <div
          style={{
            position: 'absolute',
            bottom: 2,
            right: 3,
            background: 'rgba(0,0,0,0.75)',
            color: '#fff',
            fontSize: 9,
            fontWeight: 600,
            padding: '1px 4px',
            borderRadius: 3,
            letterSpacing: 0.2,
          }}
        >
          {formatK(video.views)}
        </div>
      )}
    </div>
  );
};

const MetricCell = ({ value, label, withDivider, highlightColor, tooltipTitle, tooltipDesc }) => {
  const inner = (
    <div
      data-no-card-click={tooltipTitle ? '' : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        padding: '2px 0',
        borderLeft: withDivider ? '1px solid var(--dp-border-subtle)' : 'none',
        cursor: 'default',
      }}
    >
      <span
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: highlightColor || 'var(--dp-text-primary)',
          fontFamily: 'var(--dp-font)',
          lineHeight: 1.1,
        }}
      >
        {value}
      </span>
      <span
        style={{
          fontSize: 9,
          fontWeight: 600,
          color: 'var(--dp-text-tertiary)',
          textTransform: 'uppercase',
          letterSpacing: 0.6,
          fontFamily: 'var(--dp-font)',
        }}
      >
        {label}
      </span>
    </div>
  );
  if (tooltipTitle) {
    return (
      <DarkTooltip
        maxWidth={260}
        content={
          <div>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>{tooltipTitle}</div>
            <div style={{ fontSize: 12, lineHeight: 1.4, opacity: 0.9 }}>
              {tooltipDesc}
            </div>
          </div>
        }
      >
        {inner}
      </DarkTooltip>
    );
  }
  return inner;
};

const AdminMenu = ({ onClose, onReenrich, onDelete }) => {
  const { t } = useTranslation();
  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, zIndex: 9 }}
      />
      <div
        role="menu"
        style={{
          position: 'absolute',
          top: 32,
          right: 0,
          background: 'var(--dp-bg-elevated)',
          border: '1px solid var(--dp-border-medium)',
          borderRadius: 8,
          padding: 4,
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          zIndex: 10,
          minWidth: 160,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <button
          type="button"
          onClick={onReenrich}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 10px',
            background: 'transparent', border: 'none',
            color: 'var(--dp-text-primary)',
            fontSize: 13, cursor: 'pointer', textAlign: 'left',
            borderRadius: 6, fontFamily: 'var(--dp-font)',
          }}
        >
          <RefreshCw size={14} />
          {t('viralNiches.reenrich_button')}
        </button>
        <button
          type="button"
          onClick={onDelete}
          style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 10px',
            background: 'transparent', border: 'none',
            color: 'var(--dp-red-500)',
            fontSize: 13, cursor: 'pointer', textAlign: 'left',
            borderRadius: 6, fontFamily: 'var(--dp-font)',
          }}
        >
          <Trash2 size={14} />
          {t('viralNiches.delete_channel_title')}
        </button>
      </div>
    </>
  );
};

export default ChannelCard;
